import {
	computed,
	ComputedRef,
	onActivated,
	onDeactivated,
	onMounted,
	onUnmounted,
	Ref,
	ref,
	watch,
	effectScope,
	getCurrentScope,
} from 'vue';
import { experimentConfig } from '@/libraries/experiment/experiment-config';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers/tracking-provider';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { injectLocal, provideLocal, until } from '@vueuse/core';
import { experimentActivePlacements, getGlobalExperimentStatus, useExperimentStatus } from './useExperimentStatus';
import {
	ValidExperimentName,
	ExperimentStatus,
	ExperimentVariant,
	ExperimentTrackEventOptions,
} from '@/libraries/experiment/types';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

function getExperimentStatusKey(experimentName: ValidExperimentName) {
	return 'experimentStatus_' + experimentName;
}

/**
 *
 * This composable is used to activate an experiment based on the provided condition.
 * It checks if the user is in the sample for the experiment and activates it accordingly.
 * It also provides the experiment status and allows tracking events related to the experiment.
 *
 * NOTE: This composable must be used within the setup() function of a Vue component.
 * WARNING: Ensure that this composable is called only once per experiment
 * (do not call it in multiple components for the same experiment).
 *
 * @param experimentName
 * @param activationCondition
 * @returns
 */
function useActivateExperiment<T extends ValidExperimentName>(
	experimentName: T,
	activationCondition: Ref<boolean> | ComputedRef<boolean>
) {
	// Ensure this composable is used within setup()
	const parentScope = getCurrentScope();
	if (!parentScope) {
		throw new Error('useActivateExperiment must be called within setup().');
	}

	const isComponentActive = ref(true);

	// Create a child effect scope that ties into the calling component's lifecycle
	const scope = effectScope();

	const userStore = useUserStore();
	const { registerActivatedExperiment } = userStore;
	const { isLoggedIn, experimentsLoaded, userExperimentsInfo } = storeToRefs(userStore);

	/*prettier-ignore*/
	const { activateExperimentPlacement,
		deactivateExperimentPlacement,
		localPlacementStatus } = useExperimentStatus(experimentName);

	const { activeRoute } = storeToRefs(useRoutingStore());

	// Provide the experiment status so it can be used in child components
	provideLocal(getExperimentStatusKey(experimentName), localPlacementStatus);

	const experimentInfo = computed(() => userExperimentsInfo.value[experimentName]);

	// Check if the user is in the sample for the experiment means is either acquired from BE or it's a preferred experiment.
	const isUserAcquired = computed(() => !!experimentInfo.value?.variant);

	const isExperimentActivated = computed(() => experimentInfo.value?.activated);

	const activeVariant = computed(() => (isExperimentActivated.value ? experimentInfo.value?.variant : null));

	const isUserInAllowedRoute = computed(() => {
		const { routes, tabs } = experimentConfig[experimentName];
		if (routes.length === 0 && tabs.length === 0) return true;

		let allowed = true;

		if (tabs.length > 0) {
			allowed = tabs.some(tab => {
				return activeRoute.value?.meta?.tab === tab;
			});
		}

		if (routes.length > 0 && allowed) {
			allowed = routes.some(route => {
				return activeRoute.value?.name === route;
			});
		}

		return allowed;
	});

	async function registerExperiment() {
		// only set ready when the experiment hasn't been activated before.
		const shouldExpBeActiveCheck = isExperimentActiveCheck(
			experimentName,
			activeRoute.value?.name as unknown as boolean,
			isLoggedIn.value,
			activationCondition.value
		);

		if (!isExperimentActivated.value && shouldExpBeActiveCheck && experimentInfo.value?.variant) {
			registerActivatedExperiment(experimentName);
			triggerExperimentActivationEvent(experimentName, experimentInfo.value?.variant);
		}
	}

	function updateExperimentStatus() {
		if (isUserAcquired.value && activeVariant.value) {
			if (
				isExperimentActiveCheck(
					experimentName,
					isComponentActive.value,
					isLoggedIn.value,
					activationCondition.value
				)
			) {
				activateExperimentPlacement();
			} else {
				deactivateExperimentPlacement();
			}
		}
	}

	onMounted(async () => {
		scope.run(async () => {
			await until(experimentsLoaded).toBe(true);

			if (!isUserAcquired.value) return; // check if use was acquired to the experiment otherwise no need to activate experiment.

			await until(activationCondition).toBe(true);
			await until(isUserInAllowedRoute).toBe(true);

			watch(
				() => [
					isExperimentActivated.value,
					isComponentActive.value,
					isLoggedIn.value,
					activeVariant.value,
					activationCondition.value,
				],
				() => {
					updateExperimentStatus();
					if (!isExperimentActivated.value) {
						registerExperiment();
					}
				},
				{ immediate: true }
			);
		});
	});

	onDeactivated(() => {
		isComponentActive.value = false;
	});

	onActivated(() => {
		isComponentActive.value = true;
	});

	onUnmounted(() => {
		scope.stop();
		isComponentActive.value = false;
		deactivateExperimentPlacement();
	});

	return useExperiment(experimentName);
}

/**
 *
 * This composable is used to get the experiment status and active variant for a given experiment.
 * It provides methods to track events related to the experiment.
 *
 * Note: This composable relies on the `useActivateExperiment` composable to activate the experiment.
 *       If used within child components of a component where `useActivateExperiment` was invoked,
 *       it will utilize the status of that specific "placement."
 *
 * @param experimentName
 * @param status - 'local' to use the local status provided by `useActivateExperiment` or 'global' to use the global experiment status (if at least one experiment placement is active).
 * @returns
 */

function useExperiment<T extends ValidExperimentName>(experimentName: T, status: 'local' | 'global' = 'local') {
	const { userExperimentsInfo } = storeToRefs(useUserStore());

	const experimentInfo = computed(() => {
		const userExperiments = userExperimentsInfo.value;
		if (experimentName in userExperiments) {
			return userExperiments[experimentName];
		}
		return {
			variant: null,
			preferred: false,
			activated: false,
			affiliationShortId: null,
		};
	});

	const localExperimentStatus = getCurrentScope()
		? injectLocal<ComputedRef<ExperimentStatus | null>>(
				getExperimentStatusKey(experimentName),
				computed(() => null)
		  )
		: computed(() => null);

	const isExperimentActive = computed(() => {
		if (status === 'global') {
			return getGlobalExperimentStatus(experimentName, experimentActivePlacements.value) === 'ACTIVE';
		}
		return localExperimentStatus.value === 'ACTIVE';
	});

	const activeVariantWithControlGroup = computed<ExperimentVariant<T> | null>(() => {
		if (isExperimentActive.value) {
			return experimentInfo.value?.variant as ExperimentVariant<T>;
		}
		return null;
	});

	const activeVariant = computed(() => {
		if (activeVariantWithControlGroup.value === 'control') {
			return null;
		}
		return activeVariantWithControlGroup.value as Exclude<ExperimentVariant<T>, 'control'>;
	});

	function trackEvent({
		version = '1',
		action,
		label,
		contexts = [],
		value,
		property,
		nonInteraction = false,
	}: ExperimentTrackEventOptions<T>) {
		// not in experiment
		if (activeVariantWithControlGroup.value == null) return;

		const properties: TrackingProviderPropertiesInterface = { action, label, value, property, nonInteraction };
		if (action === 'impression') properties.nonInteraction = true;

		TrackingHelper.trackEvent('onpage_test', properties, [...contexts]);
	}

	return {
		trackEvent,
		activeVariant,
		activeVariantWithControlGroup,
		isExperimentActive,
		/*
		 * The experimentInfo contains the variant, preferred status, and activation status.
		 * It is used to determine if the user is in the sample for the experiment.
		 */
		experimentInfo,
	};
}

function isExperimentActiveCheck(
	experimentName: ValidExperimentName,
	isComponentActive: boolean,
	isLoggedIn: boolean,
	experimentActivationCondition: boolean
) {
	const userStatus = isLoggedIn ? 'LOGGED_IN' : 'LOGGED_OUT';
	const userStatusAllowed = experimentConfig?.[experimentName].userTypes.includes(userStatus);

	return isComponentActive && userStatusAllowed && experimentActivationCondition;
}

function triggerExperimentActivationEvent(experimentName: ValidExperimentName, variant: string) {
	const properties = {
		action: variant,
		label: '__ACTIVATED__',
		nonInteraction: true,
	};
	TrackingHelper.trackEvent(`EXP_${experimentName}`, properties);
	console.log('[EXPERIMENT ACTIVATED]', experimentName, variant);
}

function getExperimentStatus(experimentName: ValidExperimentName): ExperimentStatus {
	return getGlobalExperimentStatus(experimentName, experimentActivePlacements.value);
}

export { useExperiment, useActivateExperiment, getExperimentStatus };
